/**
 * Related Party Detail Panel — slide-out panel with inline editing,
 * evidence chain, and change history.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  X,
  Building2,
  User,
  Landmark,
  Pencil,
  Check,
  Trash2,
  FileText,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  RELATED_PARTY_FIELD_FORMATTERS,
  RELATED_PARTY_FIELD_LABELS,
} from "./historyFormatters";
import type { RelatedPartyEntryUI, RelatedPartyEvidenceUI } from "./types";
import { DocumentDetail } from "../../documents/DocumentDetail";

const STATUS_BADGE: Record<
  string,
  { label: string; color: "emerald" | "amber" | "blue" | "red" }
> = {
  confirmed: { label: "Confirmed", color: "emerald" },
  probable: { label: "Probable", color: "amber" },
  possible: { label: "Possible", color: "blue" },
  excluded: { label: "Excluded", color: "red" },
};

const CATEGORY_LABELS: Record<string, string> = {
  director: "Director",
  former_director: "Former director",
  shareholder: "Shareholder",
  related_body_corporate: "Related body corporate",
  director_relative: "Director relative",
  spouse_of_director: "Spouse of director",
  controlled_entity: "Controlled entity",
  associate: "Associate",
  unknown_associated_party: "Unknown associated party",
  spouse: "Spouse (Bankruptcy Act s 5)",
  de_facto_partner: "De facto partner (s 5)",
  former_spouse: "Former spouse (s 5)",
  former_de_facto_partner: "Former de facto partner (s 5)",
  relative: "Relative (s 5)",
};

const STATUS_OPTIONS: Record<string, string> = {
  confirmed: "Confirmed",
  probable: "Probable",
  possible: "Possible",
  excluded: "Excluded",
};
const REVIEW_OPTIONS: Record<string, string> = {
  system_generated: "System generated",
  under_review: "Under review",
  confirmed_by_user: "Confirmed by user",
  excluded_by_user: "Excluded by user",
};

const EVIDENCE_TYPE_LABELS: Record<string, string> = {
  director_match: "Director match",
  corporate_structure_match: "Corporate structure",
  shareholder_match: "Shareholder match",
  bank_account_match: "Bank account",
  bank_narration_match: "Bank narration",
  address_match: "Address match",
  minutes_reference: "Minutes reference",
  director_report_reference: "Director report",
  name_match: "Name match",
  corporate_record: "Corporate record",
};

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]">
      {children}
    </div>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = true,
  persistId,
  children,
}: {
  title: React.ReactNode;
  defaultOpen?: boolean;
  persistId?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = usePersistedDisclosure(persistId, defaultOpen);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-foreground/70 transition-colors hover:text-foreground"
      >
        <span>{title}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            open && "rotate-90",
          )}
        />
      </button>
      {open && children}
    </>
  );
}

function PartyIcon({ type }: { type: string }) {
  const cls = "h-4 w-4";
  if (type === "company") return <Building2 className={cls} />;
  if (type === "trust") return <Landmark className={cls} />;
  return <User className={cls} />;
}

function EditableField({
  label,
  value,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value?: string | number | null;
  field: string;
  onSave: (f: string, v: string | undefined) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const handleSave = () => {
    onSave(field, draft || undefined);
    setEditing(false);
  };
  if (editing)
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-36 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-right text-sm text-foreground">
          {String(value ?? "\u2014")}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  field: string;
  onSave: (f: string, v: string | undefined) => void;
  isViewer?: boolean;
}) {
  if (isViewer)
    return (
      <div className="flex items-center justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm text-foreground">
          {options[value] ?? value}
        </span>
      </div>
    );
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-7 w-44 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EvidenceItem({ ev }: { ev: RelatedPartyEvidenceUI }) {
  const label =
    EVIDENCE_TYPE_LABELS[ev.evidenceType] ?? ev.evidenceType.replace(/_/g, " ");
  const evPct = Math.round(ev.matchConfidenceScore * 100);
  const scoreColor =
    evPct >= 90
      ? "text-emerald-600"
      : evPct >= 70
        ? "text-amber-500"
        : "text-blue-500";
  return (
    <div className="border-b py-3 last:border-b-0">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
          {label}
        </span>
        <span
          className={cn(
            "shrink-0 text-sm font-semibold tabular-nums",
            scoreColor,
          )}
        >
          {evPct}%
        </span>
      </div>
      <p className="text-sm leading-relaxed text-foreground/80">
        {ev.evidenceSummary}
      </p>
      {ev.explanation && (
        <p className="mt-1 text-xs italic text-muted-foreground">
          {ev.explanation}
        </p>
      )}
    </div>
  );
}

interface Props {
  entry: RelatedPartyEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  isViewer?: boolean;
}

export function RelatedPartyDetailPanel({
  entry,
  open,
  onClose,
  onDeleted,
  isViewer,
}: Props) {
  const [visible, setVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.relatedParties.publicMutations
        .updateRelatedPartyField,
    ),
  });
  const deleteParty = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.relatedParties.publicMutations
        .deleteRelatedParty,
    ),
  });

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      setConfirmDelete(false);
    } else setVisible(false);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!entry) return null;

  // `updateRelatedPartyField`'s `value` arg is `v.any()` on the backend
  // since it patches whichever allow-listed field name is passed — every
  // field this panel edits (status/category/review selects, ACN/ABN, name)
  // is a plain string, so we type the value that narrowly here.
  const handleFieldSave = (field: string, value: string | undefined) => {
    updateField.mutate({
      entryId: entry._id as Id<"relatedPartyEntries">,
      field,
      value,
    });
  };
  const handleDelete = () => {
    deleteParty.mutate(
      { entryId: entry._id as Id<"relatedPartyEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  const statusCfg =
    STATUS_BADGE[entry.relationshipStatus] ?? STATUS_BADGE.possible;
  const scorePct = Math.round(entry.confidenceScore * 100);
  const scoreColor =
    scorePct >= 90
      ? "text-emerald-600"
      : scorePct >= 70
        ? "text-amber-500"
        : scorePct >= 50
          ? "text-blue-500"
          : "text-red-500";

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 transition-opacity duration-200",
          visible
            ? "bg-black/18 dark:bg-black/40"
            : "pointer-events-none bg-transparent",
        )}
        onClick={onClose}
      />
      <div
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-full max-w-lg overflow-y-auto border-l bg-background shadow-xl transition-transform duration-200 ease-out",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <PartyIcon type={entry.partyType} />
                <span className="text-xs capitalize text-muted-foreground">
                  {entry.partyType}
                </span>
              </div>
              {!isViewer && editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("legalName", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("legalName", nameDraft);
                      setEditingName(false);
                    }}
                    className="p-1 text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(entry.legalName);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-lg font-semibold leading-snug text-foreground">
                      {entry.legalName}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
              {entry.tradingNames.length > 0 && (
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Trading as: {entry.tradingNames.join(", ")}
                </div>
              )}
            </div>
            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Badge variant="soft" color={statusCfg.color} size="sm">
              {statusCfg.label}
            </Badge>
            <Badge variant="outline" size="sm">
              {CATEGORY_LABELS[entry.relationshipCategory] ??
                entry.relationshipCategory}
            </Badge>
            {entry.isManualOverride && (
              <Badge color="emerald" variant="soft" size="sm">
                Manually edited
              </Badge>
            )}
            <div className="ml-auto flex items-baseline gap-1">
              <span
                className={cn("text-xl font-bold tabular-nums", scoreColor)}
              >
                {scorePct}%
              </span>
              <span className="text-[10px] uppercase text-muted-foreground">
                conf.
              </span>
            </div>
          </div>
        </div>

        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this related party?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteParty.isPending}
              >
                {deleteParty.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-8">
          {/* Summary */}
          <SectionHeader>Summary</SectionHeader>
          {isViewer ? (
            <p className="py-2 text-sm leading-relaxed text-foreground/80">
              {entry.summary || "\u2014"}
            </p>
          ) : (
            <Textarea
              key={entry._id + "-summary"}
              defaultValue={entry.summary ?? ""}
              onBlur={(e) => {
                if (e.target.value !== (entry.summary ?? ""))
                  handleFieldSave("summary", e.target.value);
              }}
              placeholder="Describe the relationship..."
              className="mt-2 min-h-[80px] resize-y p-3 text-sm leading-relaxed"
            />
          )}
          <div className="mt-2 border-b" />

          {/* Details */}
          <SectionHeader>Details</SectionHeader>
          <SelectField
            label="Status"
            value={entry.relationshipStatus}
            options={STATUS_OPTIONS}
            field="relationshipStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Category"
            value={entry.relationshipCategory}
            options={CATEGORY_LABELS}
            field="relationshipCategory"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Review"
            value={entry.reviewStatus}
            options={REVIEW_OPTIONS}
            field="reviewStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="ACN"
            value={entry.acn}
            field="acn"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="ABN"
            value={entry.abn}
            field="abn"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          {entry.linkedPersonNames.length > 0 && (
            <div className="flex items-start justify-between gap-4 py-2">
              <span className="text-sm text-muted-foreground">
                Linked persons
              </span>
              <span className="text-right text-sm text-foreground">
                {entry.linkedPersonNames.join(", ")}
              </span>
            </div>
          )}
          {entry.basisOfRelationship.length > 0 && (
            <div className="py-2">
              <span className="text-sm text-muted-foreground">Basis</span>
              <ul className="mt-1 space-y-0.5">
                {entry.basisOfRelationship.map((b, i) => (
                  <li
                    key={i}
                    className="pl-3 text-sm text-foreground/80 before:mr-2 before:text-muted-foreground before:content-['•']"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-2 border-b" />

          {/* Evidence */}
          {entry.evidence.length > 0 && (
            <CollapsibleSection
              title={`Evidence (${entry.evidence.length})`}
              defaultOpen={false}
              persistId="related-party-detail:evidence"
            >
              {entry.evidence.map((ev, i) => (
                <EvidenceItem key={i} ev={ev} />
              ))}
            </CollapsibleSection>
          )}

          {/* Notes */}
          <SectionHeader>Practitioner notes</SectionHeader>
          {isViewer ? (
            <p className="py-2 text-sm leading-relaxed text-foreground/80">
              {entry.analystNotes || "\u2014"}
            </p>
          ) : (
            <Textarea
              key={entry._id + "-notes"}
              defaultValue={entry.analystNotes ?? ""}
              onBlur={(e) => {
                if (e.target.value !== (entry.analystNotes ?? ""))
                  handleFieldSave("analystNotes", e.target.value);
              }}
              placeholder="Add practitioner notes..."
              className="mt-2 min-h-[100px] resize-y p-3 text-sm leading-relaxed"
            />
          )}
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments && entry.sourceDocuments.length > 0 && (
            <>
              <CollapsibleSection
                title="Source documents"
                persistId="related-party-detail:source-documents"
              >
                <div className="flex flex-col gap-2 pt-2.5">
                  {entry.sourceDocuments.map((doc, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewingDocumentId(doc.documentId as Id<"documents">)
                      }
                      className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {doc.fileName || doc.documentId?.slice(0, 12) + "..."}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CollapsibleSection>
              <div className="mt-4 border-b" />
            </>
          )}

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={RELATED_PARTY_FIELD_FORMATTERS}
                fieldLabels={RELATED_PARTY_FIELD_LABELS}
                persistId="related-party-detail:history"
              />
            </div>
          )}
        </div>
      </div>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </>
  );
}
